import { useCallback, useEffect, useMemo, useState } from 'react';
import { Clapperboard, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import {
  DIALOGUE_CAMERA_MODES,
  dialogueCameraMode,
  lineFocusOptions,
  setLineFocus,
} from '@/lib/dialogueShots';
import { CharacterMapOverlay } from './CharacterMapOverlay';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const MAP_METHOD_LABELS = {
  gemini_vision: 'Characters found by AI',
  fallback_split: 'Positions estimated left to right — re-detect or check the boxes',
  manual: 'Positions adjusted by hand',
};

function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

function Chip({ active, children, onClick, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="px-2 py-1 rounded-md text-[11px] border transition-colors disabled:opacity-50"
      style={{
        background: active ? 'var(--primary)' : 'var(--bg-secondary)',
        color: active ? '#fff' : 'var(--text-primary)',
        borderColor: active ? 'var(--primary)' : 'var(--glass-border)',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Per-scene dialogue camera: where each character stands, the planned shots, who is on
 * camera for each line, and lip-synced close-ups.
 */
export function DialogueCameraDialog({
  open,
  onOpenChange,
  story,
  scene,
  characterById = {},
  onSceneChange,
  onStoryChange,
  pollJob,
}) {
  const storyId = story?.id;
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [selectedShot, setSelectedShot] = useState(null);
  const mode = dialogueCameraMode(story);

  const loadView = useCallback(async () => {
    if (!storyId || !scene?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/stories/${storyId}/dialogue-shots`);
      setView((data?.scenes || []).find((s) => s.scene_id === scene.id) || null);
    } catch (err) {
      toast.error(apiError(err, 'Could not load the dialogue shots'));
    } finally {
      setLoading(false);
    }
  }, [storyId, scene?.id]);

  useEffect(() => {
    if (open) {
      setSelectedShot(null);
      loadView();
    }
  }, [open, loadView, mode]);

  const script = useMemo(() => (Array.isArray(scene?.audio_script) ? scene.audio_script : []), [scene?.audio_script]);
  const focusOptions = useMemo(
    () => lineFocusOptions(view?.character_map, characterById),
    [view?.character_map, characterById],
  );
  const shots = view?.shots || [];
  const shot = shots.find((s) => s.shot_id === selectedShot) || null;
  const frameRect = shot?.camera?.keyframes?.[1]?.rect || shot?.camera?.keyframes?.[0]?.rect || null;

  const changeMode = async (next) => {
    if (next === mode) return;
    setBusy('mode');
    try {
      await api.patch(`/stories/${storyId}/dialogue-camera`, { mode: next });
      onStoryChange?.({ dialogue_camera: next });
    } catch (err) {
      toast.error(apiError(err, 'Could not change the dialogue camera'));
    } finally {
      setBusy('');
    }
  };

  const redetect = async () => {
    setBusy('map');
    try {
      const { data } = await api.post(`/scenes/${scene.id}/character-map`);
      setView(data);
      onSceneChange?.({ character_map: data?.character_map });
      toast.success('Found the characters again');
    } catch (err) {
      toast.error(apiError(err, 'Could not detect the characters'));
    } finally {
      setBusy('');
    }
  };

  const changeFocus = async (index, focus) => {
    const nextScript = setLineFocus(script, index, focus);
    setBusy(`line-${index}`);
    try {
      await api.put(`/stories/${storyId}/scenes/${scene.id}`, { audio_script: nextScript });
      onSceneChange?.({ audio_script: nextScript });
      await loadView();
    } catch (err) {
      toast.error(apiError(err, 'Could not save who is on camera'));
    } finally {
      setBusy('');
    }
  };

  const animate = async () => {
    setBusy('lipsync');
    try {
      const { data } = await api.post(`/stories/${storyId}/dialogue-shots/lip-sync`, { scene_ids: [scene.id] });
      toast.success(`Animating ${data?.planned_shots || 'the'} close-up${data?.planned_shots === 1 ? '' : 's'}…`);
      if (data?.job_id && pollJob) {
        pollJob(data.job_id, (err) => {
          if (err) toast.error(err);
          else toast.success('Speakers animated — the close-ups now move their lips');
          loadView();
        }, { refreshOnComplete: false });
      }
    } catch (err) {
      toast.error(apiError(err, 'Could not start the lip-sync'));
    } finally {
      setBusy('');
    }
  };

  const hasImage = Boolean(scene?.image_url);
  const mapReady = Boolean(view?.map_current);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clapperboard className="w-4 h-4" /> Dialogue camera · Scene {scene?.scene_number}
          </DialogTitle>
          <DialogDescription>
            One image per scene; the camera cuts and pushes toward whoever is speaking.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="dialogue-camera-modes">
            {DIALOGUE_CAMERA_MODES.map((m) => (
              <Chip key={m.id} active={mode === m.id} disabled={busy === 'mode'} onClick={() => changeMode(m.id)} testId={`dialogue-camera-mode-${m.id}`}>
                {m.label}
              </Chip>
            ))}
          </div>

          {mode === 'wide' ? (
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Each scene plays as one wide shot. Pick speaker close-ups to cut between characters.
            </p>
          ) : null}

          {mode !== 'wide' && !hasImage ? (
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Generate the scene image first.</p>
          ) : null}

          {mode !== 'wide' && hasImage ? (
            <>
              <CharacterMapOverlay
                imageUrl={scene.image_url}
                characterMap={mapReady ? view.character_map : null}
                characterById={characterById}
                highlightId={shot?.focus_character_id || null}
                frameRect={frameRect}
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                  {loading
                    ? 'Loading…'
                    : mapReady
                      ? MAP_METHOD_LABELS[view.character_map?.method] || ''
                      : 'Characters not located in this image yet'}
                </span>
                <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" disabled={busy === 'map'} onClick={redetect} data-testid="dialogue-camera-redetect">
                  {busy === 'map' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  {mapReady ? 'Re-detect characters' : 'Detect characters'}
                </Button>
              </div>

              {shots.length ? (
                <div className="space-y-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Shots</span>
                  <div className="flex flex-wrap gap-1.5">
                    {shots.map((s) => (
                      <Chip key={s.shot_id} active={selectedShot === s.shot_id} onClick={() => setSelectedShot(selectedShot === s.shot_id ? null : s.shot_id)} testId={`dialogue-shot-${s.shot_id}`}>
                        {s.shot_id} · {s.focus_character_id ? (characterById[s.focus_character_id] || s.speaker || 'Close-up') : 'Wide'}
                        {' '}({(s.end_seconds - s.start_seconds).toFixed(1)}s){s.video_url ? ' · lip-sync' : ''}
                      </Chip>
                    ))}
                  </div>
                </div>
              ) : null}

              {mapReady && script.length ? (
                <div className="space-y-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>On camera for each line</span>
                  {script.map((line, idx) => {
                    if (!line || typeof line !== 'object' || String(line.kind || '').toLowerCase() === 'action') return null;
                    const who = String(line.kind || '').toLowerCase() === 'narration'
                      ? 'Narrator'
                      : (characterById[line.character_id] || line.speaker || 'Character');
                    const current = line.focus_character_id || '';
                    return (
                      <div key={`focus-${idx}`} className="rounded-lg p-2 space-y-1.5" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}>
                        <p className="text-[11px] leading-snug" style={{ color: 'var(--text-primary)' }}>
                          <span className="font-semibold">{who}:</span> {line.text}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {focusOptions.map((opt) => (
                            <Chip
                              key={opt.value || 'auto'}
                              active={current === opt.value}
                              disabled={busy === `line-${idx}`}
                              onClick={() => changeFocus(idx, opt.value)}
                              testId={`line-focus-${idx}-${opt.value || 'auto'}`}
                            >
                              {opt.label}
                            </Chip>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {shots.length ? (
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t" style={{ borderColor: 'var(--glass-border)' }}>
                  <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                    Lip-sync makes the speaker&apos;s mouth move in each close-up (Seedance, uses video credits).
                  </span>
                  <Button type="button" size="sm" className="h-8 text-[11px]" style={{ background: 'var(--primary)', color: '#fff' }} disabled={busy === 'lipsync'} onClick={animate} data-testid="dialogue-camera-animate">
                    {busy === 'lipsync' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <TwinneeLogo className="w-3 h-3 mr-1" />}
                    Animate speakers
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DialogueCameraDialog;
